import { useEffect, useId, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { SystemName } from '@/components/ui/SystemName';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useNotificationRecipients } from '@/hooks/useMail';
import { isTestModeActive, useMailSettings } from '@/hooks/useMailSettings';
import { useErfasseManuelleWartung } from '@/hooks/useMaintenanceSession';
import type { ManuelleWartungResult } from '@/hooks/useMaintenanceSession';
import { useSystems } from '@/hooks/useSystems';
import type { SystemsFilter } from '@/hooks/useSystems';
import type { NotificationRecipients } from '@/types/api';

interface Props {
  customerId: string;
  open: boolean;
  onClose: () => void;
}

interface FeldFehler {
  zeitraum?: string;
  arbeiten?: string;
  durchgefuehrtVon?: string;
  empfaenger?: string;
}

/** Serverseitige Suche: erst nach dieser Pause wird neu geladen. */
const SUCH_VERZOEGERUNG_MS = 300;
/** Obergrenze von GET /systems (routes/systems.py, le=200). */
const SYSTEM_LIMIT = 200;
/**
 * Wer die Wartung gerade abschließt, rundet das Ende gern auf die Viertelstunde
 * (17:15 um 17:03 getippt). Der Server toleriert dieselben 15 Minuten.
 */
const ZUKUNFT_TOLERANZ_MS = 15 * 60 * 1000;

/** datetime-local kennt keine Zeitzone - lokale Wandzeit, auf die Minute. */
function alsDatetimeLocal(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function aufMinute(d: Date): Date {
  const x = new Date(d.getTime());
  x.setSeconds(0, 0);
  return x;
}

function vorbefuellung(): { von: string; bis: string } {
  const jetzt = aufMinute(new Date());
  const start = new Date(jetzt.getTime() - 60 * 60 * 1000);
  return { von: alsDatetimeLocal(start), bis: alsDatetimeLocal(jetzt) };
}

/** Woher die Adressen stammen, die der Server tatsächlich nehmen würde. */
type Herkunft = 'gepflegt' | 'invoice_ninja' | 'keine';

interface EmpfaengerVorschau {
  adressen: string[];
  herkunft: Herkunft;
  optOut: boolean;
}

function saubereListe(werte: unknown): string[] {
  if (!Array.isArray(werte)) return [];
  return werte
    .filter((w): w is string => typeof w === 'string')
    .map((w) => w.trim())
    .filter(Boolean);
}

/**
 * Bildet die Empfänger-Kaskade des Servers ab (run_engine/engine.py,
 * ``_resolve_recipients``): gepflegte ``notification_emails`` gewinnen; ist
 * die Liste leer, holt der Server die Mail-aktiven Invoice-Ninja-Kontakte
 * live - genau die Adressen, die dieser Endpunkt als
 * ``invoice_ninja_suggestions`` mitliefert.
 *
 * Wer hier nur ``emails`` liest, behauptet "es geht keine Mail raus", während
 * der Freitext an die Rechnungskontakte des Kunden geht (Befund 09.09.2026).
 */
export function empfaengerVorschau(
  data: NotificationRecipients | undefined
): EmpfaengerVorschau {
  if (!data) return { adressen: [], herkunft: 'keine', optOut: false };
  // `recipients` nur, falls ein älterer Server so spricht.
  const roh = data as NotificationRecipients & { recipients?: string[] };
  const optOut = Boolean(roh.opt_out);
  const gepflegt = saubereListe(roh.emails).length
    ? saubereListe(roh.emails)
    : saubereListe(roh.recipients);
  if (gepflegt.length > 0) return { adressen: gepflegt, herkunft: 'gepflegt', optOut };
  const ausInvoiceNinja = saubereListe(roh.invoice_ninja_suggestions);
  if (ausInvoiceNinja.length > 0) {
    return { adressen: ausInvoiceNinja, herkunft: 'invoice_ninja', optOut };
  }
  return { adressen: [], herkunft: 'keine', optOut };
}

export function ManuelleWartungDialog({ customerId, open, onClose }: Props): JSX.Element {
  const start = vorbefuellung();
  const [von, setVon] = useState(start.von);
  const [bis, setBis] = useState(start.bis);
  const [systemIds, setSystemIds] = useState<string[]>([]);
  const [arbeiten, setArbeiten] = useState('');
  const [durchgefuehrtVon, setDurchgefuehrtVon] = useState('');
  const [benachrichtigen, setBenachrichtigen] = useState(true);
  const [inBestaetigt, setInBestaetigt] = useState(false);
  const [wartungsstandSetzen, setWartungsstandSetzen] = useState(true);
  const [suche, setSuche] = useState('');
  const [fehler, setFehler] = useState<FeldFehler>({});
  // Nach dem Absenden bleibt der Dialog offen und zeigt, was wirklich
  // passiert ist. Ein stilles Schließen hat den fehlgeschlagenen Versand
  // früher unsichtbar gemacht.
  const [quittung, setQuittung] = useState<ManuelleWartungResult | null>(null);
  const [quittungMitMail, setQuittungMitMail] = useState(false);
  const [quittungMitStand, setQuittungMitStand] = useState(false);

  const erfassen = useErfasseManuelleWartung();
  const sucheDebounced = useDebouncedValue(suche, SUCH_VERZOEGERUNG_MS);
  const suchbegriff = sucheDebounced.trim();
  // Die Suche läuft am Server: lokal gefiltert würden bei mehr als 200
  // Systemen genau die fehlen, die man sucht.
  const systemFilter: SystemsFilter = { customerId, limit: SYSTEM_LIMIT };
  if (suchbegriff) systemFilter.search = suchbegriff;
  const systemeQuery = useSystems(systemFilter);
  const empfaengerQuery = useNotificationRecipients(customerId);
  const mailSettings = useMailSettings();

  const systeme = useMemo(
    () => systemeQuery.data?.data ?? [],
    [systemeQuery.data?.data]
  );
  /** true = der Server hat weitere Seiten; die Liste ist unvollständig. */
  const weitereSeiten = Boolean(systemeQuery.data?.links?.next);
  const vorschau = empfaengerVorschau(empfaengerQuery.data);
  const optOut = vorschau.optOut;
  const adressen = vorschau.adressen;
  const ausInvoiceNinja = vorschau.herkunft === 'invoice_ninja';

  const formId = useId();

  useEffect(() => {
    if (!open) return;
    const neu = vorbefuellung();
    setVon(neu.von);
    setBis(neu.bis);
    setSystemIds([]);
    setArbeiten('');
    setDurchgefuehrtVon('');
    setBenachrichtigen(true);
    setInBestaetigt(false);
    setWartungsstandSetzen(true);
    setSuche('');
    setFehler({});
    setQuittung(null);
    setQuittungMitMail(false);
    setQuittungMitStand(false);
    erfassen.reset();
    // Nur beim Öffnen; erfassen.reset in den Deps würde eine Schleife auslösen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Opt-out ist eine Kundenentscheidung: der Server unterdrückt die Mail
  // still (engine.py) und setzt die Quittung trotzdem. Der Schalter darf
  // deshalb gar nicht erst an sein.
  useEffect(() => {
    if (optOut) setBenachrichtigen(false);
  }, [optOut]);

  function pruefe(): FeldFehler {
    const f: FeldFehler = {};
    const vonDate = von ? new Date(von) : null;
    const bisDate = bis ? new Date(bis) : null;
    const vonOk = vonDate != null && !Number.isNaN(vonDate.getTime());
    const bisOk = bisDate != null && !Number.isNaN(bisDate.getTime());
    if (!vonOk || !bisOk || bisDate.getTime() <= vonDate.getTime()) {
      f.zeitraum = 'Das Ende muss nach dem Beginn liegen.';
    } else if (bisDate.getTime() > Date.now() + ZUKUNFT_TOLERANZ_MS) {
      f.zeitraum = 'Das Ende liegt mehr als 15 Minuten in der Zukunft.';
    }
    if (arbeiten.trim() === '') {
      f.arbeiten = 'Bitte beschreiben Sie die durchgeführten Arbeiten.';
    }
    if (durchgefuehrtVon.trim().length < 2) {
      f.durchgefuehrtVon = 'Bitte angeben, wer die Wartung durchgeführt hat.';
    }
    if (benachrichtigen && ausInvoiceNinja && !inBestaetigt) {
      f.empfaenger =
        'Bitte bestätigen Sie den Versand an die Kontakte aus Invoice Ninja.';
    }
    return f;
  }

  function onAbsenden(): void {
    const f = pruefe();
    setFehler(f);
    if (Object.keys(f).length > 0) return;
    const mitMail = benachrichtigen && !optOut;
    const mitStand = wartungsstandSetzen && systemIds.length > 0;
    // datetime-local liefert lokale Wandzeit ohne Offset; Date+toISOString
    // hält den Zeitpunkt fest (Browser-TZ, bei Colin Europe/Vienna).
    erfassen.mutate(
      {
        customerId,
        von: new Date(von).toISOString(),
        bis: new Date(bis).toISOString(),
        systemIds,
        arbeitsbeschreibung: arbeiten.trim(),
        durchgefuehrtVon: durchgefuehrtVon.trim(),
        benachrichtigen: mitMail,
        wartungsstandSetzen,
      },
      {
        onSuccess: (ergebnis) => {
          setQuittungMitMail(mitMail);
          setQuittungMitStand(mitStand);
          setQuittung(ergebnis);
        },
      }
    );
  }

  function toggleSystem(id: string, an: boolean): void {
    setSystemIds((bisher) => {
      if (an) return bisher.includes(id) ? bisher : [...bisher, id];
      return bisher.filter((x) => x !== id);
    });
  }

  const alleAn = systeme.length > 0 && systeme.every((s) => systemIds.includes(s.id));

  function onAlleKeine(): void {
    // Bezieht sich auf die ANGEZEIGTE Menge - bei aktiver Suche oder mehr als
    // 200 Systemen ist das nicht der ganze Kundenbestand.
    const angezeigt = systeme.map((s) => s.id);
    if (alleAn) setSystemIds((bisher) => bisher.filter((id) => !angezeigt.includes(id)));
    else setSystemIds((bisher) => [...new Set([...bisher, ...angezeigt])]);
  }

  const arbeitenId = `${formId}-arbeiten`;
  const arbeitenHintId = `${arbeitenId}-hint`;
  const arbeitenErrId = fehler.arbeiten ? `${arbeitenId}-err` : undefined;

  if (quittung) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title="Wartung erfasst"
        description="Das ist der Stand nach der Erfassung."
        footer={
          <Button variant="primary" data-testid="mw-quittung-schliessen" onClick={onClose}>
            Schließen
          </Button>
        }
      >
        <div data-testid="mw-quittung" className="flex flex-col gap-3 text-sm">
          <p>
            Das Wartungsfenster ist verbucht.{' '}
            {quittungMitStand
              ? `Wartungsstand gesetzt bei ${quittung.systeme_aktualisiert} von ${systemIds.length} gewählten Systemen.`
              : 'Der Wartungsstand der Systeme wurde nicht verändert - die Fälligkeit bleibt, wie sie war.'}
          </p>
          <QuittungMail
            ergebnis={quittung}
            mitMail={quittungMitMail}
            testModus={isTestModeActive(mailSettings.data)}
          />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Wartung erfassen"
      description="Eine von Hand durchgeführte Wartung nachträglich eintragen."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            data-testid="mw-absenden"
            disabled={erfassen.isPending}
            loading={erfassen.isPending}
            onClick={onAbsenden}
          >
            {erfassen.isPending
              ? 'Wird erfasst …'
              : benachrichtigen
                ? 'Erfassen und Mail senden'
                : 'Wartung erfassen'}
          </Button>
        </>
      }
    >
      <div className="flex max-h-[65vh] flex-col gap-3 overflow-y-auto pr-1">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input
            type="datetime-local"
            label="Beginn"
            data-testid="mw-von"
            value={von}
            step={60}
            hint="Ortszeit Wien; der Zeitraum steht so in der Kundenmail."
            onChange={(e) => setVon(e.target.value)}
          />
          <Input
            type="datetime-local"
            label="Ende"
            data-testid="mw-bis"
            value={bis}
            step={60}
            error={fehler.zeitraum}
            onChange={(e) => setBis(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-text">Systeme</span>
            {systeme.length > 0 && (
              <Button variant="ghost" size="sm" type="button" onClick={onAlleKeine}>
                {alleAn ? 'Keine' : 'Alle angezeigten'}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted">
            {systemIds.length} ausgewählt, {systeme.length} angezeigt
          </p>
          <Input
            type="search"
            placeholder="Systeme suchen …"
            data-testid="mw-systeme-suche"
            value={suche}
            hint="Die Suche läuft am Server über alle Systeme dieses Kunden."
            onChange={(e) => setSuche(e.target.value)}
          />
          {weitereSeiten && (
            <p data-testid="mw-systeme-gekuerzt" className="text-xs text-warning">
              Es werden nur die ersten {SYSTEM_LIMIT} Systeme angezeigt - bitte über die
              Suche einschränken.
            </p>
          )}
          <div
            data-testid="mw-systeme"
            className="max-h-48 overflow-y-auto rounded-md border border-border p-2"
            role="group"
            aria-label="Systeme dieses Kunden"
          >
            {systemeQuery.isLoading && (
              <p className="text-sm text-muted">Systeme werden geladen …</p>
            )}
            {systemeQuery.isError && (
              <p className="text-sm text-danger">Systeme konnten nicht geladen werden.</p>
            )}
            {systemeQuery.isSuccess && systeme.length === 0 && (
              <p className="text-sm text-muted">
                {suchbegriff
                  ? 'Kein System passt zu dieser Suche.'
                  : 'Für diesen Kunden sind keine Systeme gepflegt.'}
              </p>
            )}
            {systeme.map((system) => {
              const id = `${formId}-sys-${system.id}`;
              const checked = systemIds.includes(system.id);
              return (
                <label key={system.id} htmlFor={id} className="flex items-center gap-2 py-1 text-sm">
                  <input
                    id={id}
                    type="checkbox"
                    data-testid={`mw-system-${system.id}`}
                    checked={checked}
                    onChange={(e) => toggleSystem(system.id, e.target.checked)}
                  />
                  <span>
                    <SystemName system={system} />
                  </span>
                </label>
              );
            })}
          </div>
          {systemIds.length === 0 && !systemeQuery.isLoading && (
            <p className="text-xs text-muted">
              Ohne Systemauswahl wird kein Wartungsstand aktualisiert.
            </p>
          )}
          {systemIds.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="mw-wartungsstand"
                  checked={wartungsstandSetzen}
                  onChange={(e) => setWartungsstandSetzen(e.target.checked)}
                />
                Wartungsstand der gewählten Systeme setzen
              </label>
              <p className="text-xs text-muted">
                Hat dieser Kunde noch keinen automatischen Lauf, rechnet die Fälligkeit
                über den jüngsten Systemstand des Kunden: ein einzelnes nachgetragenes
                System gilt dann für den ganzen Kunden als Wartung - er verschwindet aus
                der Fälligkeitsliste und die Ankündigungsmail entfällt. Bei einer
                Teilarbeit (Telefon-Support an einem System) besser abschalten.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor={arbeitenId} className="text-sm font-medium text-text">
            Durchgeführte Arbeiten
          </label>
          <textarea
            id={arbeitenId}
            data-testid="mw-arbeiten"
            rows={5}
            value={arbeiten}
            onChange={(e) => setArbeiten(e.target.value)}
            aria-invalid={fehler.arbeiten ? true : undefined}
            aria-describedby={[arbeitenHintId, arbeitenErrId].filter(Boolean).join(' ') || undefined}
            className={[
              'w-full rounded-md border bg-surface px-3 py-2 text-sm text-text',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              fehler.arbeiten ? 'border-danger' : 'border-border',
            ].join(' ')}
          />
          {fehler.arbeiten ? (
            <p id={arbeitenErrId} className="text-xs text-danger" role="alert">
              {fehler.arbeiten}
            </p>
          ) : (
            <p id={arbeitenHintId} className="text-xs text-muted">
              Dieser Text geht an den Kunden - keine Hostnamen, IPs oder Versionsnummern.
            </p>
          )}
        </div>

        <Input
          label="Durchgeführt von"
          data-testid="mw-durchgefuehrt-von"
          value={durchgefuehrtVon}
          placeholder="z. B. Colin Urban"
          hint="Erscheint wörtlich in der Kundenmail - bitte Klarnamen eintragen."
          error={fehler.durchgefuehrtVon}
          onChange={(e) => setDurchgefuehrtVon(e.target.value)}
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="mw-benachrichtigen"
            checked={benachrichtigen}
            disabled={optOut}
            onChange={(e) => setBenachrichtigen(e.target.checked)}
          />
          Kunde benachrichtigen
        </label>

        {optOut && (
          <p data-testid="mw-opt-out" className="text-sm text-warning">
            Dieser Kunde hat Wartungs-Infos abbestellt - es geht keine Mail raus. Die
            Erfassung wird trotzdem protokolliert.
          </p>
        )}

        {benachrichtigen && !optOut && (
          <div data-testid="mw-empfaenger" className="flex flex-col gap-1 text-sm">
            {empfaengerQuery.isLoading && (
              <p className="text-muted">Empfänger werden geladen …</p>
            )}
            {empfaengerQuery.isError && (
              <p className="text-danger">Empfänger konnten nicht geladen werden.</p>
            )}
            {empfaengerQuery.isSuccess && adressen.length === 0 && (
              <p className="text-warning">
                Für diesen Kunden ist keine Empfängeradresse gepflegt - es geht keine Mail raus.
              </p>
            )}
            {adressen.length > 0 && <p>{adressen.join(', ')}</p>}
            {ausInvoiceNinja && (
              <>
                <p className="text-warning">
                  Diese Adressen sind nicht gepflegt, sondern gerade aus Invoice Ninja
                  geholt (die Rechnungskontakte des Kunden). Genau dorthin schickt der
                  Server die Mail.
                </p>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid="mw-in-bestaetigen"
                    checked={inBestaetigt}
                    onChange={(e) => setInBestaetigt(e.target.checked)}
                  />
                  Ja, an diese Kontakte aus Invoice Ninja senden
                </label>
                {fehler.empfaenger && (
                  <p className="text-xs text-danger" role="alert">
                    {fehler.empfaenger}
                  </p>
                )}
              </>
            )}
            {isTestModeActive(mailSettings.data) && (
              <p className="text-warning">
                Testmodus: die Mail geht an {mailSettings.data.redirect_address}.
              </p>
            )}
          </div>
        )}

        {erfassen.isError && (
          <div data-testid="mw-fehler">
            <ErrorAlert error={erfassen.error} />
          </div>
        )}
      </div>
    </Dialog>
  );
}

interface QuittungMailProps {
  ergebnis: ManuelleWartungResult;
  mitMail: boolean;
  testModus: boolean;
}

/**
 * Die Mail-Auskunft der Antwort im Klartext.
 *
 * Der Endpunkt liefert mail_erzeugt, mail_status, recipients,
 * effective_recipients, subject und body ausdrücklich dafür. Ohne diese
 * Anzeige blieb ein SMTP-Fehler (status 'failed') unsichtbar, obwohl
 * finished_mail_sent_at gesetzt ist und kein Pfad die Mail nachreicht.
 */
function QuittungMail({ ergebnis, mitMail, testModus }: QuittungMailProps): JSX.Element {
  if (!mitMail) {
    return (
      <p data-testid="mw-quittung-mail" className="text-muted">
        Es wurde keine Kundenmail erzeugt - die Erfassung ist nur protokolliert.
      </p>
    );
  }

  if (!ergebnis.mail_erzeugt) {
    return (
      <p data-testid="mw-quittung-mail" className="text-warning">
        Es ist KEINE Mail entstanden. Der Kunde weiß nichts davon, und es reicht sie
        auch niemand nach. Häufigste Gründe: der Kunde hat Wartungs-Infos abbestellt,
        es ist keine Empfängeradresse auffindbar, oder die Vorlage „Wartung (manuell
        erfasst)“ ist deaktiviert (Einstellungen → Mail).
      </p>
    );
  }

  const ziel = ergebnis.effective_recipients.length
    ? ergebnis.effective_recipients
    : ergebnis.recipients;

  return (
    <div data-testid="mw-quittung-mail" className="flex flex-col gap-2">
      {ergebnis.mail_status === 'sent' && (
        <p>
          Mail verschickt an {ziel.join(', ') || 'die hinterlegten Empfänger'}.
          {testModus ? ' (Testmodus - umgeleitet, der Kunde bekommt nichts.)' : ''}
        </p>
      )}
      {ergebnis.mail_status === 'failed' && (
        <p className="text-danger">
          Der Versand ist FEHLGESCHLAGEN (SMTP). Die Zeile liegt im Postausgang und
          muss dort erneut gesendet werden - von allein passiert nichts mehr.
        </p>
      )}
      {ergebnis.mail_status !== 'sent' && ergebnis.mail_status !== 'failed' && (
        <p className="text-warning">
          Die Mail liegt als Entwurf im Postausgang (Status: {ergebnis.mail_status ?? '–'})
          und ist noch nicht beim Kunden.
        </p>
      )}
      {ergebnis.subject && (
        <p className="text-muted">
          Betreff: <span className="text-text">{ergebnis.subject}</span>
        </p>
      )}
      {ergebnis.body && (
        <details>
          <summary className="cursor-pointer text-muted">Gesendeten Text anzeigen</summary>
          <pre
            data-testid="mw-quittung-text"
            className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface p-3 font-sans text-xs"
          >
            {ergebnis.body}
          </pre>
        </details>
      )}
    </div>
  );
}
